import { ChangeDetectionStrategy, Component, Input, signal, ViewChild, type OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { MediaControlService } from '@medsurf/flat-services';
import { ZodSharedValidator } from '@medsurf/flat-validators';

@Component({
    selector: 'medsurf-control-search',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './search.control.component.html',
    styleUrls: ['./search.control.component.scss'],
    standalone: false
})
export class SearchControlComponent implements OnInit {
  @Input() allowedTypes: FlatModels.PageEntityModels.PageType[] = [FlatModels.PageEntityModels.PageType.enum.Slide, FlatModels.PageEntityModels.PageType.enum.Training];
  
  @ViewChild('searchStringInputText', {static: false}) searchStringInputText: any;
  
  #isSubmitted = signal(false);
  isModal = signal(false);
  #handled = false;
  target = true;
  searchForm = new FormGroup(
    {
      searchString: new FormControl(''),
      typeFilter: new FormControl(this.allowedTypes),
      createdByMe: new FormControl(false),
      usedInFilter: new FormControl<string[]>([]),
      sortField: new FormControl(''),
      sortOrder: new FormControl(0),
      rows: new FormControl(25),
      first: new FormControl(0)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.SearchControlModels.NodeSearchSchema.pick({
          searchString: true,
          typeFilter: true,
          createdByMe: true,
          usedInFilter: true,
          sortField: true,
          sortOrder: true,
          rows: true,
          first: true,
        })
      ),
      updateOn: 'change',
    },
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public searchControlFacade: FlatFacades.SearchControlFacade,
              public mediaAuthorFacade: FlatFacades.MediaAuthorFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
              public mediaControlService: MediaControlService,
              public ref: DynamicDialogRef,
              public dynamicDialogConfig: DynamicDialogConfig) {
    if (this.dynamicDialogConfig.data) {
      if (this.dynamicDialogConfig.data.allowedTypes) {
        this.allowedTypes = structuredClone(this.dynamicDialogConfig.data.allowedTypes).reduce((arr: FlatModels.PageEntityModels.PageType[], allowedType: FlatModels.PageEntityModels.PageType) => {
          arr.push(allowedType);
          return arr;
        }, []);
      }
      if (this.dynamicDialogConfig.data.isModal) {
        this.isModal.set(this.dynamicDialogConfig.data.isModal);
      }
    }
  }
  
  ngOnInit() {
    // Fire Search
    this.onSubmit();
    
    // Autofocus
    setTimeout(() => {
      this.searchStringInputText?.inputElement?.nativeElement?.focus();
    })
  }
  
  onSubmit(resetPage = true){
    if (this.#isSubmitted() || this.searchForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    if (resetPage) this.searchForm.controls.first.setValue(0)
    
    this.searchControlFacade.requestFindNodes(this.searchForm.value as FlatModels.SearchControlModels.NodeSearch).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.searchForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onLazy(event: any){
    this.searchForm.controls.first.setValue(event?.first);
  }
  
  changeLayout(event: any) {
    this.slideAuthorFacade.requestSetLayout(event.layout);
  }
  
  onNodeClick(searchResult: FlatModels.SearchControlModels.NodeData) {
    if (this.#handled) {
      this.#handled = false;
      return;
    }
    if (!this.isModal()) {
      this.navigationControlFacade.requestNavigate(['node', searchResult.id]);
    } else {
      this.searchControlFacade.requestSetCurrentSelectedNodeResult(searchResult.id).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }
  
  onToggleBulkNode(id: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestSetNodes([id]).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  onModalSubmit(currentSelectedNode: string) {
    this.#handled = true;
    // Return Values
    this.ref.close({
      id: currentSelectedNode,
      linkText: '/node/' + currentSelectedNode,
      target: this.target ? '_blank' : '_self'
    });
  }
  
  trackBy(index: number,
          item: FlatModels.SearchControlModels.NodeData) {
    return item.id || index;
  }
}
